import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { catchError, of, switchMap } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { AddScanToULDAction } from 'src/app/core/enums/outbound-scan-gle.enum';
import {
  AddScanToULDRequest,
  OpenULORequest,
  OutboundScan,
} from 'src/app/core/models/position-operations/outbound-gle.model';
import { UserService } from 'src/app/core/services/admin/user/user.service';
import { OutboundGLEService } from 'src/app/core/services/position-operations/outbound-gle/outbound-gle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { FileUploadComponent } from '../../../file-upload/file-upload.component';
import { BUTTON_CLICK } from '../../../modal/exit-modal/exit-modal.constants';
import { IMAGE_SRC } from '../../../shared-constants';
import {
  CONST,
  CONTINUE_FLAG_MAPPING,
  TrackingNumberItem,
  UNIT_Of_MEASURE,
} from './outbound-scan-expanded-row.constant';

@Component({
  selector: 'app-outbound-scan-expanded-row',
  templateUrl: './outbound-scan-expanded-row.component.html',
  styleUrls: ['./outbound-scan-expanded-row.component.scss'],
  standalone: false,
})
export class OutboundScanExpandedRowComponent implements OnInit, OnChanges {
  @Input() expandedElement!: OutboundScan | null;
  @Input() draftRequestBody!: OpenULORequest;
  readonly CONSTANTS = { ...CONST };
  readonly SRC = IMAGE_SRC;
  readonly UNITS = UNIT_Of_MEASURE;
  readonly SpecialCharacters = SpecialCharacters;
  form!: FormGroup;
  trackingNumberData: TrackingNumberItem[] = [];
  trackingNumbers: string[] = [];
  destroy = inject(DestroyRef);
  isFileUpload: boolean = false;
  looseCount: number = 0;
  uldCount: number = 0;
  currUser = '';

  constructor(
    public readonly commonUtils: CommonUtils,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly outboundGLEService: OutboundGLEService,
    private readonly userService: UserService,
    private cdr: ChangeDetectorRef,
  ) {}

  /**
   * OnInit Life cycle hook init form
   *
   * @memberof OutboundScanExpandedRowComponent
   */
  ngOnInit(): void {
    this.looseCount = this.expandedElement?.losseCount ?? 0;
    this.uldCount = this.expandedElement?.uldCount ?? 0;

    this.userService
      .getCurrentUser$()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((user) => {
        this.currUser = user.employeeId ?? '';
      });
  }

  /**
   * onChanges Life cycle hook init form
   *
   * @memberof OutboundScanExpandedRowComponent
   */
  ngOnChanges(): void {
    this.initForm();
  }

  /**
   * Define form with form controls
   *
   * @memberof OutboundScanExpandedRowComponent
   */
  initForm() {
    this.form = this.fb.group({
      type: [null],
      trackingNumbers: [null, [Validators.required, Validators.maxLength(18)]],
      dryIceAmountOne: [null],
      dryIceAmountTwo: [''],
      unitOfMeasure: [''],
    });
  }

  /**
   * submit form data
   *
   * @memberof OutboundScanExpandedRowComponent
   */
  submit() {
    this.commonUtils.updateLoadingModal(true);
    this.outboundGLEService
      .addScanToULD(this.createRequest())
      .pipe(
        takeUntilDestroyed(this.destroy),
        switchMap((data) => {
          return of(data);
        }),
        catchError((error) => {
          if (error.error.code === 400) {
            return this.handleBadRequest(error, {
              ...this.createRequest(),
              events: [
                {
                  ...this.createRequest().events[0],
                  inspectEvents: error.error.response?.inspectEvents,
                },
              ],
            });
          }
          return of(null);
        }),
      )
      .subscribe((response) => {
        this.commonUtils.updateLoadingModal(false);
        if (response.code === 200) {
          this.isFileUpload = false;
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          this.form.reset();
          this.updateTrackingNumberFields(response);
          this.uldCount = Number(response.response.uldCount ?? 0);
          this.looseCount = Number(response.response.looseCount ?? 0);
          this.cdr.detectChanges();
        }
      });
  }

  handleBadRequest(data: any, payload: AddScanToULDRequest) {
    const inspectEvents = data.error.response.pkgList[0]?.inspectEvents;
    const message = data.error.response.pkgList[0]?.message;
    const hazmatMessage =
      data.error.response.pkgList[0]?.hazmatResponse?.message;

    if (inspectEvents) {
      const flags = Object.keys(inspectEvents).filter(
        (key) => inspectEvents[key],
      );

      if (flags.length > 0 && message) {
        const updatedPayload: AddScanToULDRequest = {
          ...payload,
          events: [
            {
              ...payload.events[0],
              inspectEvents: { ...inspectEvents },
            },
          ],
        };
        this.commonUtils.updateBanner(false);
        this.openDialog(flags, updatedPayload, message);
      } else if (flags.length === 0 && hazmatMessage) {
        this.commonUtils.updateBannerType400Error(hazmatMessage);
      } else if (flags.length === 0 && message) {
        this.commonUtils.updateBannerType400Error(message);
      } else {
        this.commonUtils.updateBannerType400Error(
          data.error?.errors[0]?.message,
        );
      }
    }

    return of(data);
  }

  openDialog(flags: string[], payload: AddScanToULDRequest, message: string) {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTANTS.WARNING.HEADER,
        body: message,
        confirmButtonText: this.CONSTANTS.WARNING.CONFIRM_BUTTON,
        rejectButtonText: this.CONSTANTS.WARNING.REJECT_BUTTON,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          const updatedPayload = { ...payload };
          flags.forEach((flag) => {
            const continueFlag = CONTINUE_FLAG_MAPPING[flag];
            updatedPayload.events.forEach((events) => {
              if (events.inspectContinueEvents) {
                (events.inspectContinueEvents as Record<string, boolean>)[
                  continueFlag
                ] = true;
              }
            });
          });
          this.commonUtils.updateLoadingModal(true);
          return this.outboundGLEService
            .addScanToULD(updatedPayload)
            .pipe(
              catchError((error) => {
                if (error?.error?.code === 400) {
                  return this.handleBadRequest(error, updatedPayload);
                }
                return of(error.error.response);
              }),
            )
            .subscribe((response) => {
              this.commonUtils.updateLoadingModal(false);
              if (response?.code === 200) {
                this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                this.form.reset();
                this.isFileUpload = false;

                this.updateTrackingNumberFields(response);
                this.uldCount = Number(response.response.uldCount ?? 0);
                this.looseCount = Number(response.response.looseCount ?? 0);
                this.cdr.detectChanges();
              }
            });
        } else {
          return of(null);
        }
      });
  }

  updateTrackingNumberFields(response: any) {
    const pkg = response.response.pkgList[0];

    if (Array.isArray(pkg.status) && Array.isArray(pkg.reason)) {
      const count = Math.max(pkg.status.length, pkg.reason.length);

      for (let i = 0; i < count; i++) {
        this.trackingNumberData.push({
          trackingNumber: pkg.trackingNumber,
          status: pkg.status[i] || '',
          reason: pkg.reason[i] || '',
          flex: pkg.flex,
        });
      }
    } else {
      // Fallback in case they're not arrays
      this.trackingNumberData.push({
        trackingNumber: pkg.trackingNumber,
        status: pkg.status as string,
        reason: pkg.reason as string,
        flex: pkg.flex,
      });
    }
  }
  createRequest(): AddScanToULDRequest {
    let selectedLoad;
    if (this.draftRequestBody) {
      selectedLoad = this.draftRequestBody;
    } else {
      selectedLoad = this.expandedElement?.rawUlo;
      if (selectedLoad) {
        const originSortCode = selectedLoad.loadKey.origin.sortCode;
        const destinationSortCode = selectedLoad.loadKey.destination.sortCode;
        const processingSortCode = selectedLoad.loadKey.processing.sortCode;

        selectedLoad.loadKey.origin.sortCode = this.mapSortCode(originSortCode);
        selectedLoad.loadKey.destination.sortCode =
          this.mapSortCode(destinationSortCode);
        selectedLoad.loadKey.processing.sortCode =
          this.mapSortCode(processingSortCode);
      }
    }

    const request: AddScanToULDRequest = {
      events: [
        {
          action: AddScanToULDAction.ADD,
          inspectEvents: {
            inspectSec1: false,
            inspectSec2: false,
            inspectUld: false,
            inspectSecDes: false,
            inspectCv: false,
            statusCheck: false,
            sortDateOffPlan: false,
            sortCodeOffPlan: false,
            loadChartOffPlan: false,
            notOnFile: false,
          },
          inspectContinueEvents: {
            inspectSec1UserContinueLoad: false,
            inspectSec2UserContinueLoad: false,
            inspectUldUserContinueLoad: false,
            inspectSecDesUserContinueLoad: false,
            inspectCvUserContinueLoad: false,
            statusCheckUserContinue: false,
            localInterceptUserContinue: false,
            sortDateOffPlanContinue: false,
            sortCodeOffPlanContinue: false,
            loadChartOffPlanContinue: false,
            notOnFileContinue: false,
          },
          trackingNumber: this.form.get('trackingNumbers')?.value,
          hazmat: this.form.get('type')?.value === 'hazmat',
          dryIce: this.form.get('type')?.value === 'dryIce',
          dryIceAmount: Number(
            `${this.form.get('dryIceAmountOne')?.value || '0'}.${this.form.get('dryIceAmountTwo')?.value || '0'}`,
          ),
          unitOfMeasure: this.form.get('unitOfMeasure')?.value,
        },
      ],
      ulo: {
        sortDate: selectedLoad?.sortDate,
        loadKey: {
          discriminator: selectedLoad?.loadKey.discriminator ?? '',
          origin: selectedLoad?.loadKey.origin,
          destination: selectedLoad?.loadKey.destination,
          processing: selectedLoad?.loadKey.processing,
          uld: selectedLoad?.loadKey.uld,
          service: selectedLoad?.loadKey.service,
          packageLocationRole: selectedLoad?.loadKey.packageLocationRole,
          disposition: selectedLoad?.loadKey.disposition,
          scanType: selectedLoad?.loadKey.scanType,
          loadChart: selectedLoad?.loadKey.loadChart,
          movement: selectedLoad?.loadKey.movement,
          dataCapture: selectedLoad?.loadKey.dataCapture,
          usps: selectedLoad?.loadKey.usps,
          carryOver: selectedLoad?.loadKey.carryOver,
          bulk: selectedLoad?.loadKey.bulk,
          loadType: selectedLoad?.loadKey.loadType,
          loadDescription: selectedLoad?.loadKey.loadDescription,
          flowType: selectedLoad?.loadKey.flowType,
          loadName: selectedLoad?.loadKey.loadName,
          upsPremier: selectedLoad?.loadKey.upsPremier,
        },
        userName: this.currUser,
      },
      fileUpload: false,
    };
    return request;
  }

  createRequestForFile(fileContents: string[]) {
    const selectedLoad = this.draftRequestBody || this.expandedElement?.rawUlo;

    // Map sort codes
    if (selectedLoad) {
      selectedLoad.loadKey.origin.sortCode = this.mapSortCode(
        selectedLoad.loadKey.origin.sortCode,
      );
      selectedLoad.loadKey.destination.sortCode = this.mapSortCode(
        selectedLoad.loadKey.destination.sortCode,
      );
      selectedLoad.loadKey.processing.sortCode = this.mapSortCode(
        selectedLoad.loadKey.processing.sortCode,
      );
    }

    const baseEvent = {
      action: AddScanToULDAction.ADD,
      inspectEvents: {
        inspectSec1: false,
        inspectSec2: false,
        inspectUld: false,
        inspectSecDes: false,
        inspectCv: false,
        statusCheck: false,
        sortDateOffPlan: false,
        sortCodeOffPlan: false,
        loadChartOffPlan: false,
        notOnFile: false,
      },
      inspectContinueEvents: {
        inspectSec1UserContinueLoad: false,
        inspectSec2UserContinueLoad: false,
        inspectUldUserContinueLoad: false,
        inspectSecDesUserContinueLoad: false,
        inspectCvUserContinueLoad: false,
        statusCheckUserContinue: false,
        localInterceptUserContinue: false,
        sortDateOffPlanContinue: false,
        sortCodeOffPlanContinue: false,
        loadChartOffPlanContinue: false,
        notOnFileContinue: false,
      },
      hazmat: false,
      dryIce: false,
    };

    const events = fileContents.map((trackNo) => ({
      ...baseEvent,
      trackingNumber: trackNo,
    }));

    const request: AddScanToULDRequest = {
      events,
      ulo: {
        sortDate: selectedLoad?.sortDate,
        loadKey: {
          discriminator: selectedLoad?.loadKey.discriminator ?? '',
          origin: selectedLoad?.loadKey.origin,
          destination: selectedLoad?.loadKey.destination,
          processing: selectedLoad?.loadKey.processing,
          uld: selectedLoad?.loadKey.uld,
          service: selectedLoad?.loadKey.service,
          packageLocationRole: selectedLoad?.loadKey.packageLocationRole,
          disposition: selectedLoad?.loadKey.disposition,
          scanType: selectedLoad?.loadKey.scanType,
          loadChart: selectedLoad?.loadKey.loadChart,
          movement: selectedLoad?.loadKey.movement,
          dataCapture: selectedLoad?.loadKey.dataCapture,
          usps: selectedLoad?.loadKey.usps,
          carryOver: selectedLoad?.loadKey.carryOver,
          bulk: selectedLoad?.loadKey.bulk,
          loadType: selectedLoad?.loadKey.loadType,
          loadDescription: selectedLoad?.loadKey.loadDescription,
          flowType: selectedLoad?.loadKey.flowType,
          loadName: selectedLoad?.loadKey.loadName,
          upsPremier: selectedLoad?.loadKey.upsPremier,
        },
        userName: this.currUser,
      },
      fileUpload: true,
    };
    return request;
  }

  mapSortCode(sortCode: string): string {
    if (/^\d+$/.test(sortCode)) return sortCode;

    if (sortCode === 'S') return '01';
    if (sortCode === 'M') return '02';
    if (sortCode === 'D') return '03';
    if (sortCode === 'T') return '04';
    if (sortCode === 'N') return '05';
    if (sortCode === 'P') return '06';
    if (sortCode === 'L') return '07';
    if (sortCode === 'A') return '08';
    return '00';
  }

  /**
   * Used to show Remove tracking number confirm dialog
   *
   * @param {string} trackingNumber
   * @param {number} index
   * @memberof OutboundScanExpandedRowComponent
   */
  removeTrackingNumberConfirmDialog(trackingNumber: string, index: number) {
    this.commonUtils
      .openWarningModal({
        header: CONST.REMOVE_TRACKING_NUMBER.HEADER,
        body: CONST.REMOVE_TRACKING_NUMBER.BODY,
        extraText: trackingNumber,
        confirmButtonText: CONST.REMOVE_TRACKING_NUMBER.YES,
        rejectButtonText: CONST.REMOVE_TRACKING_NUMBER.NO,
        extraClass: 'large-confirm-btn',
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.trackingNumberData.splice(index, 1);
        }
      });
  }

  /**
   * Used to open file upload modal
   *
   * @memberof OutboundScanExpandedRowComponent
   */
  fileUpload() {
    this.isFileUpload = true;
    this.dialog
      .open(FileUploadComponent, {
        data: { trackingNumber: true },
      })
      .afterClosed()
      .subscribe(() => {
        // let fileUploadResponse: FileUploadResponse = {
        //   deviceName: '',
        //   numberOfRowsUploaded: 0,
        //   accepted: 0,
        //   rejected: 0,
        //   nonProcessed: 0,
        //   rejectedDueToInspect: '',
        //   resultMessages: [],
        //   inspectLines: [],
        // };

        // forkJoin(vals$).subscribe((returnVal) => {
        //   returnVal.forEach((response) => {
        //     fileUploadResponse = {
        //       deviceName: response.response.deviceName ?? '',
        //       numberOfRowsUploaded:
        //         fileUploadResponse.numberOfRowsUploaded +
        //         (response.response.accepted ?? 0) +
        //         (response.response.rejected ?? 0),
        //       accepted:
        //         fileUploadResponse.accepted + (response.response.accepted ?? 0),
        //       rejected:
        //         fileUploadResponse.rejected + (response.response.rejected ?? 0),
        //       nonProcessed:
        //         (fileUploadResponse.nonProcessed ?? 0) +
        //         (response.response.nonProcessed ?? 0),
        //       rejectedDueToInspect:
        //         (fileUploadResponse.rejectedDueToInspect ?? '') +
        //         response.response.rejectedDueToInspect,
        //       resultMessages: [
        //         ...(fileUploadResponse.resultMessages ?? ''),
        //         ...(response.response.resultMessages ?? ''),
        //       ],
        //       inspectLines: [
        //         ...(fileUploadResponse.inspectLines ?? ''),
        //         ...(response.response.inspectLines ?? ''),
        //       ],
        //     };

        //     this.uldCount =
        //       this.uldCount + Number(response.response.uldCount ?? 0);
        //     this.looseCount =
        //       this.looseCount + Number(response.response.looseCount ?? 0);
        //   });
        // });

        // this.commonUtils.updateBannerType(
        //   BANNER_TYPE.RESULTS_FROM_UPLOAD,
        //   null,
        //   fileUploadResponse,
        // );

        this.cdr.detectChanges();
      });
  }

  /**
   * Set tracking numbers from file
   *
   * @param {[]} fileData
   * @memberof OutboundScanExpandedRowComponent
   */
  setTrackingNumberFromFile(fileData: []) {
    fileData.forEach((dataItem, index) => {
      const rawValue = Object.values(dataItem)[0];

      if (typeof rawValue === 'string' && rawValue.trim()) {
        const tracking = rawValue.trim();

        // TODO figure out these details
        this.trackingNumberData.push({
          trackingNumber: tracking,
          status: '',
          reason: '',
          flex: '',
        });

        this.trackingNumbers.push(tracking);
      } else {
        console.warn(`Invalid tracking value at row ${index}:`, dataItem);
      }
    });
  }
}
